import { useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { PageContainer } from "@/layouts/PageContainer";
import { Search } from "@/components/ui/Search";
import { Filter } from "@/components/ui/Filter";
import { DataTable } from "@/components/tables/DataTable";
import { Pagination } from "@/components/ui/Pagination";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { Avatar } from "@/components/ui/Avatar";
import { Dropdown } from "@/components/ui/Dropdown";
import { Drawer } from "@/components/ui/Drawer";
import { LoadingState } from "@/components/feedback/LoadingState";
import { ErrorState } from "@/components/feedback/ErrorState";
import { ConfirmDialog } from "@/components/modals/ConfirmDialog";
import { Textarea } from "@/components/ui/Textarea";
import { useDebounce } from "@/hooks/useDebounce";
import { useDriver, useDriversList, useSetDriverStatus } from "@/features/drivers/useDrivers";
import { toast } from "@/store/toast.store";
import { formatDate, formatINR, formatNumber } from "@/utils/format";
import type { Driver, DriverStatus } from "@/types/domain";

const STATUS_OPTIONS: { label: string; value: DriverStatus }[] = [
  { label: "Pending KYC", value: "PENDING_KYC" },
  { label: "Active", value: "ACTIVE" },
  { label: "Suspended", value: "SUSPENDED" },
  { label: "Inactive", value: "INACTIVE" },
  { label: "Rejected", value: "REJECTED" },
];

export default function Drivers() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pendingSuspend, setPendingSuspend] = useState<Driver | null>(null);
  const [suspendReason, setSuspendReason] = useState("");
  const debouncedSearch = useDebounce(search);

  const query = {
    page,
    limit: 20,
    search: debouncedSearch || undefined,
    status: (status || undefined) as DriverStatus | undefined,
  };
  const drivers = useDriversList(query);
  const setDriverStatus = useSetDriverStatus();

  const columns = useMemo<ColumnDef<Driver, unknown>[]>(
    () => [
      {
        header: "Driver",
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <Avatar name={row.original.name ?? row.original.phone} size={28} />
            <span className="font-medium">{row.original.name ?? "—"}</span>
          </div>
        ),
      },
      { header: "Phone", accessorKey: "phone" },
      { header: "Vehicle", accessorFn: (d) => d.vehicleNumber ?? "—" },
      { header: "Status", cell: ({ row }) => <StatusBadge status={row.original.status} /> },
      { header: "KYC", cell: ({ row }) => <StatusBadge status={row.original.kycStatus} /> },
      { header: "Rating", accessorFn: (d) => (d.ratingCount > 0 ? `★ ${d.rating.toFixed(1)}` : "—") },
      { header: "Trips", accessorFn: (d) => formatNumber(d.totalTrips) },
      { header: "₹/km", accessorFn: (d) => `₹${d.perKmRate}` },
      { header: "Joined", accessorFn: (d) => formatDate(d.createdAt) },
      {
        header: "",
        id: "actions",
        cell: ({ row }) => (
          <Dropdown
            trigger={<span className="text-ink-faint">⋯</span>}
            options={
              row.original.status === "SUSPENDED"
                ? [{ label: "Reactivate", value: "activate" }]
                : [{ label: "Suspend", value: "suspend", danger: true }]
            }
            onSelect={(value) => {
              if (value === "suspend") {
                setSuspendReason("");
                setPendingSuspend(row.original);
              }
              if (value === "activate") {
                setDriverStatus.mutate(
                  { id: row.original.id, status: "ACTIVE" },
                  { onSuccess: () => toast.success("Driver activated") },
                );
              }
            }}
          />
        ),
      },
    ],
    [setDriverStatus],
  );

  return (
    <PageContainer
      title="Drivers"
      description="All registered drivers and their current status."
      actions={
        <>
          <Search value={search} onChange={setSearch} placeholder="Search driver, phone…" />
          <Filter label="Status" value={status} options={STATUS_OPTIONS} onChange={setStatus} />
        </>
      }
    >
      <div className="card">
        <DataTable
          columns={columns}
          data={drivers.data?.items ?? []}
          isLoading={drivers.isLoading}
          error={drivers.error}
          onRetry={() => drivers.refetch()}
          onRowClick={(d) => setSelectedId(d.id)}
          rowKey={(d) => d.id}
          emptyTitle="No drivers found"
          emptyDescription="Try a different search or filter."
        />
        {drivers.data && (
          <Pagination page={page} pageSize={20} total={drivers.data.total} onPageChange={setPage} />
        )}
      </div>

      <ConfirmDialog
        open={!!pendingSuspend}
        title={`Suspend ${pendingSuspend?.name ?? "driver"}?`}
        description="They won't be able to go online or accept rides until reactivated. The reason is shown to the driver."
        confirmLabel="Suspend"
        confirmDisabled={suspendReason.trim().length === 0}
        loading={setDriverStatus.isPending}
        onCancel={() => setPendingSuspend(null)}
        onConfirm={() => {
          if (!pendingSuspend) return;
          setDriverStatus.mutate(
            { id: pendingSuspend.id, status: "SUSPENDED", reason: suspendReason.trim() },
            {
              onSuccess: () => {
                toast.success("Driver suspended");
                setPendingSuspend(null);
              },
            },
          );
        }}
      >
        <Textarea
          placeholder="Reason for suspension (required)"
          rows={3}
          value={suspendReason}
          onChange={(e) => setSuspendReason(e.target.value)}
        />
      </ConfirmDialog>

      <DriverDetailDrawer driverId={selectedId} onClose={() => setSelectedId(null)} />
    </PageContainer>
  );
}

function DriverDetailDrawer({ driverId, onClose }: { driverId: string | null; onClose: () => void }) {
  const driver = useDriver(driverId ?? undefined);

  return (
    <Drawer
      open={!!driverId}
      onClose={onClose}
      title={driver.data?.name ?? "Driver"}
      subtitle={driver.data?.phone}
    >
      {driver.isLoading && <LoadingState label="Loading driver…" />}
      {driver.error && <ErrorState error={driver.error} onRetry={() => driver.refetch()} />}
      {driver.data && (
        <div className="flex flex-col gap-5">
          <div className="flex items-center gap-2">
            <StatusBadge status={driver.data.status} />
            <StatusBadge status={driver.data.availability} />
            <StatusBadge status={driver.data.kycStatus} />
          </div>

          {driver.data.vehicle && (
            <div className="text-sm text-ink-muted">
              Vehicle: {driver.data.vehicle.registrationNumber}
              {driver.data.vehicle.model ? ` · ${driver.data.vehicle.model}` : ""}
            </div>
          )}

          <div>
            <h4 className="mb-2 text-sm font-semibold text-ink">Earnings</h4>
            {driver.data.wallet ? (
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs text-ink-faint">Lifetime earnings</p>
                  <p className="font-medium text-ink">{formatINR(driver.data.wallet.lifetimeNet)}</p>
                </div>
                <div>
                  <p className="text-xs text-ink-faint">Cash commission due</p>
                  <p className="font-medium text-ink">{formatINR(driver.data.wallet.cashCommissionDue)}</p>
                </div>
                <div>
                  <p className="text-xs text-ink-faint">Payout pending</p>
                  <p className="font-medium text-ink">{formatINR(driver.data.wallet.payoutPending)}</p>
                </div>
                <div>
                  <p className="text-xs text-ink-faint">Prepaid balance</p>
                  <p className="font-medium text-ink">{formatINR(driver.data.wallet.prepaidBalance)}</p>
                </div>
              </div>
            ) : (
              <p className="text-xs text-ink-faint">No earnings yet — this driver hasn't completed a paid ride.</p>
            )}
          </div>

          <p className="text-sm text-ink-muted">
            {driver.data.ranking ? (
              <>
                Leaderboard rank{" "}
                <span className="font-medium text-ink">#{driver.data.ranking.rank}</span>{" "}
                (score {driver.data.ranking.score.toFixed(1)})
              </>
            ) : (
              <span className="text-xs text-ink-faint">Not ranked yet.</span>
            )}
          </p>

          <div>
            <h4 className="mb-2 text-sm font-semibold text-ink">Recent rides</h4>
            {driver.data.recentRides.length === 0 && (
              <p className="text-xs text-ink-faint">No rides yet.</p>
            )}
            <ul className="flex flex-col gap-2">
              {driver.data.recentRides.map((r, i) => (
                <li
                  key={i}
                  className="flex items-center justify-between rounded-lg border border-border px-3 py-2 text-sm"
                >
                  <p className="font-medium text-ink">{r.code}</p>
                  <div className="text-right">
                    <p className="font-medium text-ink">{r.fare ? formatINR(r.fare.total) : "—"}</p>
                    <StatusBadge status={r.status} />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </Drawer>
  );
}
